import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
export let lastModified = undefined;
export let frontmatter = {
  "title": "Package and Task Graphs",
  "description": "Turborepo builds a Task Graph based on your configuration and repository structure."
};
export let structuredData = {
  "contents": [{
    "heading": "package-graph",
    "content": "The Package Graph is the structure of your monorepo created by your package manager. When you install Internal Packages into each other, Turborepo will automatically identify those dependency relationships to build a foundational understanding of your Workspace."
  }, {
    "heading": "package-graph",
    "content": "This sets the groundwork for the Task Graph, where you'll define how tasks relate to each other."
  }, {
    "heading": "task-graph",
    "content": "In turbo.json, you express how tasks relate to each other. You can think of these relationships as\ndependencies between tasks, but we have a more formal name for them: the Task Graph."
  }, {
    "heading": "task-graph",
    "content": "You can generate a visualization of the task graph for your tasks using the\n--graph flag."
  }, {
    "heading": "task-graph",
    "content": "Turborepo uses a data structure called a directed acyclic graph (DAG) to\nunderstand your repository and its tasks. A graph is made up of \"nodes\" and\n\"edges\". In the Task Graph, the nodes are tasks and the edges are the\ndependencies between tasks. A directed graph indicates that the edges\nconnecting each node have a direction, so if Task A points to Task B, we can say\nthat Task A depends on Task B. The direction of the edge depends on which task\ndepends on which."
  }, {
    "heading": "task-graph",
    "content": "For example, let's say you have a monorepo with an application in ./apps/web that\ndepends on two packages: @repo/ui and @repo/utils:"
  }, {
    "heading": "task-graph",
    "content": "You also have a build task that depends on ^build:"
  }, {
    "heading": "task-graph",
    "content": "Turborepo will build a task graph like this:"
  }, {
    "heading": "transit-nodes",
    "content": "A challenge when building a Task Graph is handling nested dependencies. For\nexample, let's say your monorepo has a docs app that depends on the ui\npackage, which depends on the core package:"
  }, {
    "heading": "transit-nodes",
    "content": "Let's assume the docs app and the core package each have a build task, but\nthe ui package does not. You also have a turbo.json that configures the\nbuild task the same way as above with \"dependsOn\": [\"^build\"]. When you run\nturbo run build, what would you expect to happen?"
  }, {
    "heading": "transit-nodes",
    "content": "Turborepo will build this Task Graph:"
  }, {
    "heading": "transit-nodes",
    "content": "You can think of this graph in a series of steps:"
  }, {
    "heading": "transit-nodes",
    "content": "The docs app only depends on ui."
  }, {
    "heading": "transit-nodes",
    "content": "The ui package does not have a build script."
  }, {
    "heading": "transit-nodes",
    "content": "The ui package's dependencies have a build script, so the task graph knows to include those."
  }, {
    "heading": "transit-nodes",
    "content": "Turborepo calls the ui package a Transit Node in this scenario, because it\ndoesn't have its own build script. Since it doesn't have a build script,\nTurborepo won't execute anything for it, but it's still part of the graph for\nthe purpose of including its own dependencies."
  }, {
    "heading": "transit-nodes-as-entry-points",
    "content": "What if the docs/ package didn't implement the build task? What would\nyou expect to happen in this case? Should the ui and core packages still\nexecute their build tasks? Should anything happen here?"
  }, {
    "heading": "transit-nodes-as-entry-points",
    "content": "Turborepo's mental model is that all nodes in the Task Graph are the same. In other words,\nTransit Nodes are included in the graph regardless of where they appear in the graph.\nThis model can have unexpected consequences. For example, let's say you've configured\nyour build task to depend on ^test:"
  }, {
    "heading": "transit-nodes-as-entry-points",
    "content": "Let's say your monorepo has many apps and many packages. All packages have\ntest tasks, but only one app has a build task. Turborepo's mental model\nsays that when you run turbo run build, even if an app doesn't implement build\nthe test task of all packages that are dependencies will show up in the graph."
  }],
  "headings": [{
    "id": "package-graph",
    "content": "Package Graph"
  }, {
    "id": "task-graph",
    "content": "Task Graph"
  }, {
    "id": "transit-nodes",
    "content": "Transit Nodes"
  }, {
    "id": "transit-nodes-as-entry-points",
    "content": "Transit Nodes as entry points"
  }]
};
import __img0 from "./../../../public/images/docs/simple-task-graph.png";
import __img1 from "./../../../public/images/docs/transitive-nodes.png";
import {File, Folder, Files} from '#components/files';
import {Callout} from '#components/callout';
export const toc = [{
  depth: 2,
  url: "#package-graph",
  title: _jsx(_Fragment, {
    children: "Package Graph"
  })
}, {
  depth: 2,
  url: "#task-graph",
  title: _jsx(_Fragment, {
    children: "Task Graph"
  })
}, {
  depth: 3,
  url: "#transit-nodes",
  title: _jsx(_Fragment, {
    children: "Transit Nodes"
  })
}, {
  depth: 4,
  url: "#transit-nodes-as-entry-points",
  title: _jsx(_Fragment, {
    children: "Transit Nodes as entry points"
  })
}];
function _createMdxContent(props) {
  const _components = {
    a: "a",
    code: "code",
    em: "em",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    img: "img",
    li: "li",
    p: "p",
    pre: "pre",
    span: "span",
    strong: "strong",
    ul: "ul",
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h2, {
      id: "package-graph",
      children: "Package Graph"
    }), "\n", _jsxs(_components.p, {
      children: ["The Package Graph is the structure of your monorepo created by your package manager. When you install ", _jsx(_components.a, {
        href: "/docs/core-concepts/internal-packages",
        children: "Internal Packages"
      }), " into each other, Turborepo will automatically identify those dependency relationships to build a foundational understanding of your Workspace."]
    }), "\n", _jsxs(_components.p, {
      children: ["This sets the groundwork for the Task Graph, where you'll define how ", _jsx(_components.strong, {
        children: "tasks"
      }), " relate to each other."]
    }), "\n", _jsx(_components.h2, {
      id: "task-graph",
      children: "Task Graph"
    }), "\n", _jsxs(_components.p, {
      children: ["In ", _jsx(_components.code, {
        children: "turbo.json"
      }), ", you express how tasks relate to each other. You can think of these relationships as\ndependencies between tasks, but we have a more formal name for them: the Task Graph."]
    }), "\n", _jsx(Callout, {
      type: "good-to-know",
      children: _jsxs(_components.p, {
        children: ["You can generate a visualization of the task graph for your tasks using ", _jsxs(_components.a, {
          href: "/docs/reference/run#--graph-file-type",
          children: ["the\n", _jsx(_components.code, {
            children: "--graph"
          }), " flag"]
        }), "."]
      })
    }), "\n", _jsxs(_components.p, {
      children: ["Turborepo uses a data structure called a ", _jsx(_components.a, {
        href: "https://en.wikipedia.org/wiki/Directed_acyclic_graph",
        children: "directed acyclic graph (DAG)"
      }), " to\nunderstand your repository and its tasks. A graph is made up of \"nodes\" and\n\"edges\". In the Task Graph, the nodes are tasks and the edges are the\ndependencies between tasks. A ", _jsx(_components.em, {
        children: "directed"
      }), " graph indicates that the edges\nconnecting each node have a direction, so if Task A points to Task B, we can say\nthat Task A depends on Task B. The direction of the edge depends on which task\ndepends on which."]
    }), "\n", _jsxs(_components.p, {
      children: ["For example, let's say you have a monorepo with an application in ", _jsx(_components.code, {
        children: "./apps/web"
      }), " that\ndepends on two packages: ", _jsx(_components.code, {
        children: "@repo/ui"
      }), " and ", _jsx(_components.code, {
        children: "@repo/utils"
      }), ":"]
    }), "\n", _jsxs(Files, {
      children: [_jsx(Folder, {
        name: "apps",
        defaultOpen: true,
        children: _jsx(Folder, {
          name: "web"
        })
      }), _jsxs(Folder, {
        name: "packages",
        defaultOpen: true,
        children: [_jsx(Folder, {
          name: "ui"
        }), _jsx(Folder, {
          name: "utils"
        })]
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["You also have a ", _jsx(_components.code, {
        children: "build"
      }), " task that depends on ", _jsx(_components.code, {
        children: "^build"
      }), ":"]
    }), "\n", _jsx(_Fragment, {
      children: _jsx(_components.pre, {
        className: "shiki shiki-themes css-variables css-variables",
        style: {
          "--shiki-light": "var(--shiki-foreground)",
          "--shiki-dark": "var(--shiki-foreground)",
          "--shiki-light-bg": "var(--shiki-background)",
          "--shiki-dark-bg": "var(--shiki-background)"
        },
        tabIndex: "0",
        title: "./turbo.json",
        icon: "<svg viewBox=\"0 0 24 24\"><path d=\"M 6,1 C 4.354992,1 3,2.354992 3,4 v 16 c 0,1.645008 1.354992,3 3,3 h 12 c 1.645008,0 3,-1.354992 3,-3 V 8 7 A 1.0001,1.0001 0 0 0 20.707031,6.2929687 l -5,-5 A 1.0001,1.0001 0 0 0 15,1 h -1 z m 0,2 h 7 v 3 c 0,1.645008 1.354992,3 3,3 h 3 v 11 c 0,0.564129 -0.435871,1 -1,1 H 6 C 5.4358712,21 5,20.564129 5,20 V 4 C 5,3.4358712 5.4358712,3 6,3 Z M 15,3.4140625 18.585937,7 H 16 C 15.435871,7 15,6.5641288 15,6 Z\" fill=\"currentColor\" /></svg>",
        children: _jsxs(_components.code, {
          children: [_jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "{"
            })
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "  \"tasks\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " {"
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "    \"build\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " {"
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "      \"dependsOn\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " ["
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: "\"^build\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "]"
            })]
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "    }"
            })
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "  }"
            })
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "}"
            })
          })]
        })
      })
    }), "\n", _jsx(_components.p, {
      children: "Turborepo will build a task graph like this:"
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.img, {
        alt: "Task graph visualization. The diagram has one node at the top named \"apps/web\" with two lines that connect to other nodes, \"packages/ui\" and \"packages/utils\" respectively.",
        placeholder: "blur",
        src: __img0
      })
    }), "\n", _jsx(_components.h3, {
      id: "transit-nodes",
      children: "Transit Nodes"
    }), "\n", _jsxs(_components.p, {
      children: ["A challenge when building a Task Graph is handling nested dependencies. For\nexample, let's say your monorepo has a ", _jsx(_components.code, {
        children: "docs"
      }), " app that depends on the ", _jsx(_components.code, {
        children: "ui"
      }), "\npackage, which depends on the ", _jsx(_components.code, {
        children: "core"
      }), " package:"]
    }), "\n", _jsxs(Files, {
      children: [_jsx(Folder, {
        name: "apps",
        defaultOpen: true,
        children: _jsx(Folder, {
          name: "docs"
        })
      }), _jsxs(Folder, {
        name: "packages",
        defaultOpen: true,
        children: [_jsx(Folder, {
          name: "ui"
        }), _jsx(Folder, {
          name: "core"
        })]
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["Let's assume the ", _jsx(_components.code, {
        children: "docs"
      }), " app and the ", _jsx(_components.code, {
        children: "core"
      }), " package each have a ", _jsx(_components.code, {
        children: "build"
      }), " task, but\nthe ", _jsx(_components.code, {
        children: "ui"
      }), " package does not. You also have a ", _jsx(_components.code, {
        children: "turbo.json"
      }), " that configures the\n", _jsx(_components.code, {
        children: "build"
      }), " task the same way as above with ", _jsx(_components.code, {
        children: "\"dependsOn\": [\"^build\"]"
      }), ". When you run\n", _jsx(_components.code, {
        children: "turbo run build"
      }), ", what would you expect to happen?"]
    }), "\n", _jsx(_components.p, {
      children: "Turborepo will build this Task Graph:"
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.img, {
        alt: "A Task Graph visualization with a Transit Node. The diagram has one node at the top named \"apps/doc\" with a line that connects to a \"packages/ui\" node. This node does not have a \"build\" task. The \"packages/ui\" node has another line to a \"packages/core\" node that does have a \"build\" task.",
        placeholder: "blur",
        src: __img1
      })
    }), "\n", _jsx(_components.p, {
      children: "You can think of this graph in a series of steps:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["The ", _jsx(_components.code, {
          children: "docs"
        }), " app only depends on ", _jsx(_components.code, {
          children: "ui"
        }), "."]
      }), "\n", _jsxs(_components.li, {
        children: ["The ", _jsx(_components.code, {
          children: "ui"
        }), " package does ", _jsx(_components.strong, {
          children: "not"
        }), " have a build script."]
      }), "\n", _jsxs(_components.li, {
        children: ["The ", _jsx(_components.code, {
          children: "ui"
        }), " package's ", _jsx(_components.em, {
          children: "dependencies"
        }), " have a ", _jsx(_components.code, {
          children: "build"
        }), " script, so the task graph knows to include those."]
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["Turborepo calls the ", _jsx(_components.code, {
        children: "ui"
      }), " package a Transit Node in this scenario, because it\ndoesn't have its own ", _jsx(_components.code, {
        children: "build"
      }), " script. Since it doesn't have a ", _jsx(_components.code, {
        children: "build"
      }), " script,\nTurborepo won't execute anything for it, but it's still part of the graph for\nthe purpose of including its own dependencies."]
    }), "\n", _jsx(_components.h4, {
      id: "transit-nodes-as-entry-points",
      children: "Transit Nodes as entry points"
    }), "\n", _jsxs(_components.p, {
      children: ["What if the ", _jsx(_components.code, {
        children: "docs/"
      }), " package didn't implement the ", _jsx(_components.code, {
        children: "build"
      }), " task? What would\nyou expect to happen in this case? Should the ", _jsx(_components.code, {
        children: "ui"
      }), " and ", _jsx(_components.code, {
        children: "core"
      }), " packages still\nexecute their build tasks? Should ", _jsx(_components.em, {
        children: "anything"
      }), " happen here?"]
    }), "\n", _jsxs(_components.p, {
      children: ["Turborepo's mental model is that all nodes in the Task Graph are the same. In other words,\nTransit Nodes are included in the graph regardless of where they appear in the graph.\nThis model can have unexpected consequences. For example, let's say you've configured\nyour ", _jsx(_components.code, {
        children: "build"
      }), " task to depend on ", _jsx(_components.code, {
        children: "^test"
      }), ":"]
    }), "\n", _jsx(_Fragment, {
      children: _jsx(_components.pre, {
        className: "shiki shiki-themes css-variables css-variables",
        style: {
          "--shiki-light": "var(--shiki-foreground)",
          "--shiki-dark": "var(--shiki-foreground)",
          "--shiki-light-bg": "var(--shiki-background)",
          "--shiki-dark-bg": "var(--shiki-background)"
        },
        tabIndex: "0",
        title: "./turbo.json",
        icon: "<svg viewBox=\"0 0 24 24\"><path d=\"M 6,1 C 4.354992,1 3,2.354992 3,4 v 16 c 0,1.645008 1.354992,3 3,3 h 12 c 1.645008,0 3,-1.354992 3,-3 V 8 7 A 1.0001,1.0001 0 0 0 20.707031,6.2929687 l -5,-5 A 1.0001,1.0001 0 0 0 15,1 h -1 z m 0,2 h 7 v 3 c 0,1.645008 1.354992,3 3,3 h 3 v 11 c 0,0.564129 -0.435871,1 -1,1 H 6 C 5.4358712,21 5,20.564129 5,20 V 4 C 5,3.4358712 5.4358712,3 6,3 Z M 15,3.4140625 18.585937,7 H 16 C 15.435871,7 15,6.5641288 15,6 Z\" fill=\"currentColor\" /></svg>",
        children: _jsxs(_components.code, {
          children: [_jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "{"
            })
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "  \"tasks\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " {"
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "    \"build\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " {"
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "      \"dependsOn\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " ["
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: "\"^test\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "]"
            })]
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "    }"
            })
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "  }"
            })
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "}"
            })
          })]
        })
      })
    }), "\n", _jsxs(_components.p, {
      children: ["Let's say your monorepo has many apps and many packages. All packages have\n", _jsx(_components.code, {
        children: "test"
      }), " tasks, but only one app has a ", _jsx(_components.code, {
        children: "build"
      }), " task. Turborepo's mental model\nsays that when you run ", _jsx(_components.code, {
        children: "turbo run build"
      }), ", even if an app doesn't implement ", _jsx(_components.code, {
        children: "build"
      }), "\nthe ", _jsx(_components.code, {
        children: "test"
      }), " task of all packages that are dependencies will show up in the graph."]
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = props.components || ({});
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
